import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArchiveRestore,
  CheckCircle2,
  Database,
  FileCheck2,
  Loader2,
  PlayCircle,
  RefreshCcw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

const currency = (value) => `LKR ${Number(value || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ passed, warning = false }) => {
  if (warning) return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Warning</Badge>;
  return passed
    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Passed</Badge>
    : <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Failed</Badge>;
};

const ERPValidation = () => {
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairingLegacy, setRepairingLegacy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [previewResult, reportResult] = await Promise.allSettled([
        api.get('/admin/erp-validation/preview'),
        api.get('/admin/erp-validation/report'),
      ]);
      if (previewResult.status === 'fulfilled') setPreview(previewResult.value.data);
      if (reportResult.status === 'fulfilled') setReport(reportResult.value.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the validation centre');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runValidation = async () => {
    if (confirmation !== 'RESET AND VALIDATE') {
      toast.error('Type RESET AND VALIDATE exactly');
      return;
    }
    const confirmed = window.confirm(
      'A verified backup will be created first. Existing company business data will then be replaced with the full validation dataset. Continue?'
    );
    if (!confirmed) return;
    setRunning(true);
    try {
      const response = await api.post('/admin/erp-validation/reset-seed', {
        confirmation_code: confirmation,
        reset_existing_data: true,
      });
      setReport(response.data.report);
      setConfirmation('');
      toast.success(`Validation completed. Recovery backup: ${response.data.recovery_backup_id}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Full lifecycle validation failed');
    } finally {
      setRunning(false);
    }
  };

  const auditCurrentData = async () => {
    setAuditing(true);
    try {
      const response = await api.post('/admin/erp-validation/audit');
      setReport((current) => ({ ...(current || {}), integrity: response.data }));
      toast.success(`Audit completed with status: ${response.data.status}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Audit failed');
    } finally {
      setAuditing(false);
    }
  };

  const recomputeBalances = async () => {
    setRepairing(true);
    try {
      const preview = await api.get('/admin/erp-validation/recompute-balances/preview');
      const count = preview.data?.change_count || 0;
      if (!count) {
        toast.success('All cached balances already match the journal ledger');
        return;
      }
      const confirmed = window.prompt(`${count} balance(s) will change as of ${preview.data.as_of_date}. Type RECOMPUTE BALANCES to apply the previewed changes.`);
      if (confirmed !== 'RECOMPUTE BALANCES') return;
      const response = await api.post('/admin/erp-validation/recompute-balances', {
        confirmation_code: confirmed,
        expected_change_count: count,
        as_of_date: preview.data.as_of_date,
      });
      setReport((current) => ({ ...(current || {}), integrity: response.data.integrity }));
      toast.success('Cached balances were recomputed from the journal ledger');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Balance recomputation failed');
    } finally {
      setRepairing(false);
    }
  };

  const repairLegacyCorrections = async () => {
    setRepairingLegacy(true);
    try {
      const previewResult = await api.get('/admin/erp-validation/legacy-corrections/preview');
      const repairPreview = previewResult.data;
      if (!repairPreview?.repairable_count) {
        const blocked = repairPreview?.blocked_count || 0;
        toast.info(blocked ? `${blocked} date issue(s) require manual review; no safe automatic repair was applied.` : 'No legacy correction chains require repair');
        return;
      }
      const lines = (repairPreview.items || [])
        .filter((item) => item.repairable)
        .map((item) => `${item.entry_number || item.journal_id}: ${item.current_date} → ${item.proposed_date}`)
        .join('\n');
      const confirmed = window.prompt(
        `${repairPreview.repairable_count} safely linked correction chain(s) will be date-normalised. No records or amounts will be deleted.\n\n${lines}\n\nType REPAIR LEGACY CORRECTIONS to continue.`
      );
      if (confirmed !== 'REPAIR LEGACY CORRECTIONS') return;
      const response = await api.post('/admin/erp-validation/legacy-corrections/repair', {
        confirmation_code: confirmed,
        preview_token: repairPreview.preview_token,
      });
      setReport((current) => ({ ...(current || {}), integrity: response.data.integrity }));
      toast.success(`${response.data.chains_repaired} legacy correction chain(s) repaired. Backup: ${response.data.recovery_backup_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Legacy correction repair failed');
    } finally {
      setRepairingLegacy(false);
    }
  };

  const integrity = report?.integrity;
  const controls = report?.control_totals?.checks || [];
  const pnl = report?.reports?.profit_and_loss;
  const balanceSheet = report?.reports?.balance_sheet;
  const cashFlow = report?.reports?.cash_flow;
  const failedControls = controls.filter((item) => !item.passed);

  if (loading) {
    return <div className="flex min-h-[420px] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="erp-validation-centre">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">System Administration</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">ERP Validation Centre</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Back up, reset, seed and independently verify a complete purchasing, inventory, sales, returns,
            PayHere, payroll and double-entry accounting lifecycle.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={repairLegacyCorrections} disabled={repairingLegacy}>
            {repairingLegacy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Repair Legacy Corrections
          </Button>
          <Button variant="outline" onClick={auditCurrentData} disabled={auditing}>
            {auditing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck2 className="mr-2 h-4 w-4" />}
            Audit Current Data
          </Button>
          <Button variant="outline" onClick={recomputeBalances} disabled={repairing}>
            {repairing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
            Recompute Balances
          </Button>
        </div>
      </div>

      <Alert className="border-red-200 bg-red-50">
        <AlertTriangle className="h-5 w-5 text-red-700" />
        <AlertTitle className="text-red-900">Destructive validation workflow</AlertTitle>
        <AlertDescription className="text-red-800">
          {preview?.destructive_reset_enabled
            ? 'This isolated validation environment permits a protected backup, reset and seed.'
            : (preview?.destructive_reset_reason || 'Destructive validation is blocked on live company data.')}
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> Full-lifecycle scenario</CardTitle>
            <CardDescription>The run covers every major financial and operational control path.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            {(preview?.scenario || []).map((item) => (
              <div key={item} className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span>{item}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ArchiveRestore className="h-5 w-5" /> Run validation</CardTitle>
            <CardDescription>Type the confirmation phrase exactly.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder="RESET AND VALIDATE"
              autoComplete="off"
            />
            <Button
              className="w-full bg-red-700 text-white hover:bg-red-800"
              onClick={runValidation}
              disabled={running || !preview?.destructive_reset_enabled || confirmation !== 'RESET AND VALIDATE'}
            >
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              Backup, Reset & Validate
            </Button>
            <p className="text-xs leading-5 text-slate-500">Use the recovery backup from System Admin if the seeded environment must be rolled back.</p>
          </CardContent>
        </Card>
      </div>

      {integrity && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardHeader className="pb-2"><CardDescription>Integrity status</CardDescription></CardHeader>
              <CardContent className="flex items-center gap-2 text-2xl font-black">
                {integrity.passed ? <ShieldCheck className="h-7 w-7 text-emerald-600" /> : <XCircle className="h-7 w-7 text-red-600" />}
                {String(integrity.status || 'unknown').toUpperCase()}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Checks passed</CardDescription></CardHeader>
              <CardContent className="text-2xl font-black">{integrity.summary?.passed_checks || 0}/{integrity.summary?.checks || 0}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Errors</CardDescription></CardHeader>
              <CardContent className="text-2xl font-black text-red-700">{integrity.summary?.errors || 0}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Warnings</CardDescription></CardHeader>
              <CardContent className="text-2xl font-black text-amber-700">{integrity.summary?.warnings || 0}</CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Accounting and operational controls</CardTitle>
              <CardDescription>Each control is calculated from current database records.</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead><tr className="border-b text-xs uppercase tracking-wide text-slate-500"><th className="py-3">Control</th><th>Issues</th><th>Status</th></tr></thead>
                <tbody>
                  {(integrity.checks || []).map((check) => (
                    <tr key={check.name} className="border-b last:border-0">
                      <td className="py-3 font-medium text-slate-900">{check.name}</td>
                      <td>{check.issue_count || 0}</td>
                      <td><StatusBadge passed={check.passed} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {((integrity.errors || []).length > 0 || (integrity.warnings || []).length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Audit details</CardTitle>
                <CardDescription>Exact records requiring repair or review.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-3">
                  <h3 className="font-semibold text-red-700">Errors</h3>
                  {(integrity.errors || []).map((issue, index) => (
                    <div key={`error-${issue.check}-${index}`} className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <p className="font-medium text-red-900">{issue.check}</p>
                      {(issue.examples || [issue]).map((example, exampleIndex) => (
                        <pre key={exampleIndex} className="mt-2 overflow-x-auto whitespace-pre-wrap text-xs text-red-800">{JSON.stringify(example, null, 2)}</pre>
                      ))}
                    </div>
                  ))}
                  {(integrity.errors || []).length === 0 && <p className="text-sm text-slate-500">No errors.</p>}
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-amber-700">Warnings</h3>
                  {(integrity.warnings || []).map((issue, index) => (
                    <div key={`warning-${issue.check}-${index}`} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="font-medium text-amber-900">{issue.check}</p>
                      {(issue.examples || [issue]).map((example, exampleIndex) => (
                        <pre key={exampleIndex} className="mt-2 overflow-x-auto whitespace-pre-wrap text-xs text-amber-800">{JSON.stringify(example, null, 2)}</pre>
                      ))}
                    </div>
                  ))}
                  {(integrity.warnings || []).length === 0 && <p className="text-sm text-slate-500">No warnings.</p>}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {controls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Known-answer control totals</CardTitle>
            <CardDescription>
              The validation dataset has predetermined accounting results. Every expected value must equal the generated report.
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead><tr className="border-b text-xs uppercase tracking-wide text-slate-500"><th className="py-3">Control</th><th>Expected</th><th>Actual</th><th>Status</th></tr></thead>
              <tbody>
                {controls.map((control) => (
                  <tr key={control.name} className="border-b last:border-0">
                    <td className="py-3 font-medium capitalize text-slate-900">{control.name.replaceAll('_', ' ')}</td>
                    <td>{currency(control.expected)}</td>
                    <td>{currency(control.actual)}</td>
                    <td><StatusBadge passed={control.passed} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {failedControls.length > 0 && <p className="mt-3 text-sm font-semibold text-red-700">{failedControls.length} known-answer control(s) failed.</p>}
          </CardContent>
        </Card>
      )}

      {pnl && balanceSheet && cashFlow && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader><CardTitle>Profit & Loss</CardTitle><CardDescription>{pnl.period?.start_date} to {pnl.period?.end_date}</CardDescription></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total revenue</span><strong>{currency(pnl.income?.total)}</strong></div>
              <div className="flex justify-between"><span>Total expenses</span><strong>{currency(pnl.expenses?.total)}</strong></div>
              <div className="flex justify-between border-t pt-2 text-base"><span>Net profit</span><strong>{currency(pnl.net_profit)}</strong></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Balance Sheet</CardTitle><CardDescription>As at {balanceSheet.as_of_date}</CardDescription></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total assets</span><strong>{currency(balanceSheet.total_assets)}</strong></div>
              <div className="flex justify-between"><span>Liabilities + equity</span><strong>{currency(balanceSheet.total_liabilities_equity)}</strong></div>
              <div className="flex justify-between border-t pt-2"><span>Difference</span><strong>{currency(balanceSheet.difference)}</strong></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Cash Flow</CardTitle><CardDescription>{cashFlow.period?.start_date} to {cashFlow.period?.end_date}</CardDescription></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Opening cash</span><strong>{currency(cashFlow.opening_cash_balance)}</strong></div>
              <div className="flex justify-between"><span>Net change</span><strong>{currency(cashFlow.net_change_in_cash)}</strong></div>
              <div className="flex justify-between border-t pt-2"><span>Closing cash</span><strong>{currency(cashFlow.closing_cash_balance)}</strong></div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ERPValidation;
